import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import "../components/VocabTooltip.css";
import "./QuestChestRewardModal.css";
import {
  buildQuestRewardGemImageUrl,
  getQuestCharacterPrideWord,
  getQuestRewardPotion,
} from "../utils/questRewardDisplay";

const AUTO_CLOSE_SECONDS = 5;

function GrantMiniature({ grant }) {
  if (!grant || grant.kind === "relic") return null;

  if (grant.kind === "gem") {
    return (
      <span className="vocab-tooltip-wrap quest-chest-grant quest-chest-grant--gem">
        <span className="quest-chest-grant__qty">{grant.quantity}</span>
        <img
          src={buildQuestRewardGemImageUrl(grant.itemId)}
          alt=""
          className="quest-chest-grant__image"
          decoding="sync"
        />
        <span className="vocab-tooltip vocab-tooltip--below" role="tooltip">
          {grant.label}
        </span>
      </span>
    );
  }

  const potion = getQuestRewardPotion(grant.itemId);
  return (
    <span className="vocab-tooltip-wrap quest-chest-grant quest-chest-grant--potion">
      <span className="quest-chest-grant__qty">{grant.quantity}</span>
      {potion?.image ? (
        <img
          src={potion.image}
          alt=""
          className="quest-chest-grant__image"
          decoding="sync"
        />
      ) : (
        <span className="quest-chest-grant__fallback" aria-hidden="true">
          ⚗
        </span>
      )}
      {grant.label ? (
        <span className="vocab-tooltip vocab-tooltip--below" role="tooltip">
          {grant.label}
        </span>
      ) : null}
    </span>
  );
}

export default function QuestChestRewardModal({
  open,
  characterId,
  characterName,
  grants = [],
  relicWon = false,
  relicId = "",
  relicLabel = "",
  collectionId = "",
  collectionTitle = "",
  onClose,
  onNavigateToTreasures,
}) {
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);
  const onCloseRef = useRef(onClose);
  const onNavigateRef = useRef(onNavigateToTreasures);
  const relicWonRef = useRef(relicWon);
  const relicFocusRef = useRef({ collectionId, relicId });

  const visibleGrants = grants.filter((grant) => grant.kind !== "relic");
  const prideWord = getQuestCharacterPrideWord(characterId);

  useEffect(() => {
    onCloseRef.current = onClose;
    onNavigateRef.current = onNavigateToTreasures;
    relicWonRef.current = relicWon;
    relicFocusRef.current = { collectionId, relicId };
  });

  const navigateToWonRelic = useCallback(() => {
    const { collectionId: focusCollectionId, relicId: focusRelicId } =
      relicFocusRef.current;
    if (focusCollectionId && focusRelicId) {
      onNavigateRef.current?.({
        collectionId: focusCollectionId,
        relicId: focusRelicId,
      });
      return;
    }
    onNavigateRef.current?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let remaining = AUTO_CLOSE_SECONDS;
    setSecondsLeft(remaining);

    const intervalId = window.setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);

      if (remaining <= 0) {
        window.clearInterval(intervalId);
        if (relicWonRef.current) {
          navigateToWonRelic();
        }
        onCloseRef.current?.();
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [open, navigateToWonRelic]);

  const handleClose = () => {
    if (relicWon) {
      navigateToWonRelic();
    }
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="quest-chest-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="quest-chest-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quest-chest-modal-headline"
      >
        <button
          type="button"
          className="quest-chest-modal__close"
          onClick={handleClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <div className="quest-chest-modal__body">
          <p id="quest-chest-modal-headline" className="quest-chest-modal__headline">
            Quête accomplie !
          </p>

          <p className="quest-chest-modal__pride">
            {characterName} est {prideWord} de vous !
          </p>

          <p className="quest-chest-modal__subtitle">
            Vous obtenez la récompense suivante :
          </p>

          <div className="quest-chest-modal__grants-row">
            {visibleGrants.map((grant) => (
              <GrantMiniature key={`${grant.kind}-${grant.itemId}`} grant={grant} />
            ))}
          </div>

          {relicWon ? (
            <div className="quest-chest-modal__relic-slot">
              <p className="quest-chest-modal__relic-name">{relicLabel}</p>
              <p className="quest-chest-modal__relic-message">
                Incroyable ! Vous avez découvert une relique de la collection{" "}
                {collectionTitle}. Elle vous attend dans la Salle des trésors !
              </p>
            </div>
          ) : null}
        </div>

        {!relicWon ? (
          <p className="quest-chest-modal__no-relic">
            Dommage, pas de relique cette fois-ci !
          </p>
        ) : null}

        <div className="quest-chest-modal__footer">
          <button type="button" className="quest-chest-modal__dismiss" onClick={handleClose}>
            Fermer
          </button>
          <p className="quest-chest-modal__countdown">
            Fermeture automatique dans… {secondsLeft}
          </p>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
